/**
 * Choosing a point on a map.
 *
 * This is the one screen in Beeline that shows a map, and the reason it is allowed to is the
 * distinction the whole product turns on: **a map is a fine way to choose a point and a terrible
 * way to walk to one.** Nothing here navigates. You place a crosshair, name what is there,
 * optionally photograph it, and then the map is gone and it is an arrow again.
 *
 * It backs three things that used to be three different half-features:
 *
 *   pin      save it on this device, for coming back to the car later
 *   link     hand it to somebody as a guided-arrival URL
 *   rally    put it in front of a whole room as the point everyone walks to
 *
 * All three are the same act — put a crosshair somewhere and say what it is — so they are the
 * same screen, with a different button at the bottom.
 */

import { formatDistance, haversineDistance, type LatLng } from '@beeline/shared';

import { MAX_LABEL_LENGTH, normalizeLabel, type Destination } from '../lib/destination';
import { el } from '../lib/dom';
import { createPhotoInput, type CapturedPhoto } from '../lib/photo';
import { createMapView, fetchMapConfig, type MapView } from '../ui/map';
import { spinner } from '../ui/feedback';
import { SensorHub, type SensorState } from '../sensors';

export interface PlacePickerOptions {
  title: string;
  body: string;
  /** The label on the button that finishes the job. */
  confirmLabel: string;
  /**
   * What the confirm button says while the crosshair is on top of you.
   *
   * "Save a place" and "save where I am standing" are different promises, and a button that
   * makes the wrong one is how the old flow ended up committing people to *here* on a screen
   * that was about to offer them a map.
   */
  hereLabel?: string;
  /** Suggested names. "My car" beats "spot 1", so the suggestions carry real weight. */
  suggestions?: readonly string[];
  defaultLabel: string;
  /** Where to open. Falls back to the live fix, then to a "find me" prompt. */
  start?: LatLng | null;
  /** Whether to offer a photo. Off for arrival links: a photo does not fit in a URL fragment. */
  allowPhoto?: boolean;
  onConfirm: (destination: Destination) => void;
  onCancel: () => void;
}

export interface PlacePicker {
  element: HTMLElement;
  destroy: () => void;
}

/**
 * Mount the picker.
 *
 * Returns immediately with a loading shell; the map arrives once the server has said whether it
 * has tiles at all. A deployment with no tile source configured is a supported state — every
 * caller has a path that does not need a map, and this screen says so rather than hanging.
 */
export function placePicker(options: PlacePickerOptions): PlacePicker {
  const element = el('main', { class: 'screen screen--map' });

  let map: MapView | null = null;
  let sensors: SensorHub | null = null;
  let photo: CapturedPhoto | null = null;
  let destroyed = false;

  /** The last fix, kept so "centre on me" and the distance readout both have something to use. */
  let fix: SensorState['fix'] = null;

  element.replaceChildren(
    el(
      'div',
      { class: 'screen screen--centered' },
      spinner(),
      el('p', { class: 'screen__body', text: 'Loading the map…' }),
    ),
  );

  const labelInput = el('input', {
    class: 'field__input',
    type: 'text',
    name: 'label',
    enterkeyhint: 'done',
    maxlength: String(MAX_LABEL_LENGTH),
    placeholder: options.defaultLabel,
    'aria-label': 'What is here',
  }) as HTMLInputElement;

  const distanceHint = el('p', { class: 'place__hint' });
  const photoPreview = el('img', {
    class: 'place__photo',
    hidden: true,
    alt: '',
  }) as HTMLImageElement;

  /** Says why a photo did not stick. Silence would read as "the button is broken". */
  const photoNote = el('p', { class: 'field__hint', role: 'status', hidden: true });

  const photoInput = createPhotoInput((captured) => {
    photo = captured;
    photoPreview.hidden = captured === null;
    if (captured) photoPreview.src = captured.dataUrl;
    photoButton.textContent = captured ? 'Retake the photo' : 'Add a photo';

    // `capturePhoto` returns null for a file it could not read or could not get small enough.
    // Either way the user tapped a button and nothing happened, which needs saying out loud.
    photoNote.hidden = captured !== null;
    photoNote.textContent = captured
      ? ''
      : 'That picture could not be used. Try a photo rather than a screenshot, or one taken further back.';
  });

  const photoButton = el(
    'button',
    { class: 'button button--ghost button--block', type: 'button' },
    'Add a photo',
  );
  photoButton.addEventListener('click', () => photoInput.click());

  const centreOnMe = el(
    'button',
    { class: 'button button--ghost button--small', type: 'button' },
    'Centre on me',
  );
  centreOnMe.addEventListener('click', () => {
    if (fix) map?.panTo({ lat: fix.lat, lng: fix.lng }, 18);
  });

  const confirm = el(
    'button',
    { class: 'button button--primary button--block', type: 'button', 'data-action': 'confirm' },
    options.confirmLabel,
  );

  confirm.addEventListener('click', () => {
    const at = map?.centre();
    if (!at) return;

    options.onConfirm({
      lat: at.lat,
      lng: at.lng,
      label: normalizeLabel(labelInput.value, options.defaultLabel),
      pinnedAt: Date.now(),
      // The crosshair is exact by construction. What it is *pointing at* is only as good as the
      // imagery, but that is a different kind of uncertainty from a GPS fix and claiming a
      // metre figure here would be inventing one.
      accuracy: 0,
      photo: photo?.dataUrl ?? null,
    });
  });

  const cancel = el(
    'button',
    { class: 'button button--ghost button--block', type: 'button' },
    'Back',
  );
  cancel.addEventListener('click', () => options.onCancel());

  const suggestionRow = options.suggestions?.length
    ? el(
        'div',
        { class: 'chips' },
        ...options.suggestions.map((suggestion) =>
          el(
            'button',
            {
              class: 'chip chip--action',
              type: 'button',
              onclick: () => {
                labelInput.value = suggestion;
                labelInput.focus();
              },
            },
            suggestion,
          ),
        ),
      )
    : null;

  /** Redraw the "42 m from you" line under the map, and keep the button honest about it. */
  const updateHint = (centre: LatLng): void => {
    centreOnMe.toggleAttribute('disabled', !fix);

    if (!fix) {
      distanceHint.textContent = 'Drag the map to put the crosshair on the spot.';
      confirm.textContent = options.confirmLabel;
      return;
    }

    const away = haversineDistance({ lat: fix.lat, lng: fix.lng }, centre);
    const here = away < 12;
    distanceHint.textContent = here
      ? 'Right about where you are standing.'
      : `${formatDistance(away)} from you — drag to adjust.`;
    confirm.textContent = here ? (options.hereLabel ?? options.confirmLabel) : options.confirmLabel;
  };

  const build = (config: { layers: string[]; maxZoom: number }, start: LatLng): void => {
    if (destroyed) return;

    const view = createMapView({
      centre: start,
      layers: config.layers,
      maxZoom: config.maxZoom,
      you: fix ? { lat: fix.lat, lng: fix.lng } : null,
      accuracyMetres: fix?.accuracy ?? null,
      onMove: (centre) => updateHint(centre),
    });
    map = view;

    element.replaceChildren(
      el(
        'header',
        { class: 'place__head' },
        el('h1', { class: 'screen__title', text: options.title }),
        el('p', { class: 'screen__body', text: options.body }),
      ),

      view.element,

      el(
        'div',
        { class: 'place__panel' },
        el('div', { class: 'place__row' }, distanceHint, centreOnMe),

        el(
          'label',
          { class: 'field' },
          el('span', { class: 'field__label', text: 'What is here' }),
          labelInput,
        ),
        suggestionRow,

        options.allowPhoto === false
          ? null
          : el('div', { class: 'place__photo-row' }, photoButton, photoPreview, photoNote),
        photoInput,

        el('div', { class: 'screen__actions' }, confirm, cancel),
      ),
    );

    updateHint(start);
  };

  const showNoMap = (): void => {
    element.replaceChildren(
      el(
        'div',
        { class: 'screen screen--centered' },
        el('h1', { class: 'screen__title', text: 'No map here' }),
        el('p', {
          class: 'screen__body',
          text: 'This Beeline has no map source configured, so a point has to be picked by standing on it.',
        }),
        el(
          'button',
          {
            class: 'button button--ghost button--block',
            type: 'button',
            onclick: () => options.onCancel(),
          },
          'Back',
        ),
      ),
    );
  };

  // The map wants to open somewhere useful. In order of preference: where the caller said, where
  // the device is, and — if neither — wherever the last fix eventually turns up.
  const hub = new SensorHub({
    onChange: (state) => {
      fix = state.fix;
      map?.setYou(
        state.fix ? { lat: state.fix.lat, lng: state.fix.lng } : null,
        state.fix?.accuracy ?? null,
      );
    },
  });
  sensors = hub;
  hub.start();

  /** Kept so backing out of the picker before the map opens does not leave them running. */
  let poll: number | null = null;
  let fallback: number | null = null;

  const stopWaiting = (): void => {
    if (poll !== null) window.clearInterval(poll);
    if (fallback !== null) window.clearTimeout(fallback);
    poll = null;
    fallback = null;
  };

  void fetchMapConfig().then((config) => {
    if (destroyed) return;
    if (!config) {
      showNoMap();
      return;
    }

    const start = options.start ?? (fix ? { lat: fix.lat, lng: fix.lng } : null);
    if (start) {
      build(config, start);
      return;
    }

    // No position yet. Wait briefly for one rather than opening on null island, but never
    // hang: a device with location denied still gets a usable map, just not a centred one.
    let settled = false;
    const open = (at: LatLng): void => {
      stopWaiting();
      if (settled || destroyed) return;
      settled = true;
      build(config, at);
    };

    poll = window.setInterval(() => {
      if (fix) open({ lat: fix.lat, lng: fix.lng });
    }, 250);

    fallback = window.setTimeout(() => open({ lat: 37.9838, lng: 23.7275 }), 6000);
  });

  return {
    element,
    destroy: () => {
      destroyed = true;
      stopWaiting();
      map?.destroy();
      map = null;
      sensors?.stop();
      sensors = null;
    },
  };
}
